import React from 'react';
import { Plus } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { Modal } from '../../../components/common/Modal';
import { useToast } from '../../../components/common/Toast';
import { useWeightUnit } from '../../../hooks/useWeightUnit';
import { usePermissions } from '../../../hooks/usePermissions';
import { useSubscription } from '../../../hooks/useSubscription';
import { useLanguage } from '../../../features/language/useLanguage';
import { machineService } from '../../../services/machineService';
import { roasterService } from '../../../services/roasterService';
import type { MachineFormData } from '../../../types/machine';
import type { RoasterFormData } from '../../../types/roaster';
import { MachineForm } from '../../production/MachineForm';
import { RoasterForm } from '../../production/RoasterForm';
import { useWorkOrderReferenceData } from '../hooks/useWorkOrderReferenceData';
import type { QuotaExceededPayload } from '../quotaError';
import { BulkWorkOrderCard } from './BulkWorkOrderCard';
import { BulkScheduleConfirmModal } from './BulkScheduleConfirmModal';
import { BulkMissingFieldsModal } from './BulkMissingFieldsModal';
import { MAX_BULK_CARDS, useBulkWorkOrders } from './useBulkWorkOrders';

interface Props {
  onCancel: () => void;
  onQuotaExceeded: (payload: QuotaExceededPayload) => void;
  onDone: (message: string) => void;
  /** Fired once the first draft exists — the page locks the mode toggle then. */
  onStarted: () => void;
  readOnly?: boolean;
}

export const BulkWorkOrderPanel: React.FC<Props> = ({
  onCancel,
  onQuotaExceeded,
  onDone,
  onStarted,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const { unit, setUnit } = useWeightUnit();
  const { showToast } = useToast();
  const { can } = usePermissions();
  const { tier: subTier, machineTypeQuota } = useSubscription();
  const reference = useWorkOrderReferenceData();
  const bulk = useBulkWorkOrders({ onQuotaExceeded, onDone });

  const [isMachineModalOpen, setIsMachineModalOpen] = React.useState(false);
  const [isRoasterModalOpen, setIsRoasterModalOpen] = React.useState(false);
  const [machineDraftName, setMachineDraftName] = React.useState('');
  const [roasterDraftName, setRoasterDraftName] = React.useState('');

  const hasCreatedAny = bulk.cards.some((c) => c.workOrder);
  React.useEffect(() => {
    if (hasCreatedAny) onStarted();
  }, [hasCreatedAny, onStarted]);

  // Hide the "+ Create…" autocomplete option when the user only has read on
  // the underlying resource — otherwise clicking it 403s on POST mid-flow.
  const canCreateMachine = can('machines', 'write');
  const canCreateRoaster = can('roasters', 'write');
  const isAtMachineTypeLimit =
    subTier !== 'plus' &&
    machineTypeQuota !== null &&
    machineTypeQuota.limit >= 0 &&
    machineTypeQuota.used >= machineTypeQuota.limit;

  const machineOptions = React.useMemo(
    () =>
      reference.machines
        .slice()
        .filter((machine) => machine.isActive)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((machine) => ({
          id: machine.id,
          label: machine.name,
          text: machine.name,
          value: machine.id,
        })),
    [reference.machines],
  );

  const roasterOptions = React.useMemo(
    () =>
      reference.roasters
        .slice()
        .filter((roaster) => roaster.isActive)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((roaster) => ({ id: roaster.id, label: roaster.name, text: roaster.name })),
    [reference.roasters],
  );

  const handleSaveMachine = async (data: MachineFormData) => {
    try {
      const created = await machineService.create(data);
      reference.addMachine(created);
      setIsMachineModalOpen(false);
      setMachineDraftName('');
      showToast(t('workOrders.form.toast.machineCreated'), 'success');
    } catch (error) {
      showToast((error as Error).message || t('workOrders.form.toast.machineCreateFailed'), 'error');
    }
  };

  const handleSaveRoaster = async (data: RoasterFormData) => {
    try {
      const created = await roasterService.create(data);
      reference.addRoaster(created);
      setIsRoasterModalOpen(false);
      setRoasterDraftName('');
      showToast(t('workOrders.form.toast.roasterCreated'), 'success');
    } catch (error) {
      showToast((error as Error).message || t('workOrders.form.toast.roasterCreateFailed'), 'error');
    }
  };

  if (reference.isLoading) return <LoadingSpinner text={t('workOrders.form.loading')} />;

  const isDraftStage = bulk.stage === 'draft';
  const atMax = bulk.cards.length >= MAX_BULK_CARDS;

  return (
    <>
      <div className="space-y-4" data-test-id="wo-bulk-panel">
        {bulk.errorBanner && (
          <p
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-test-id="wo-bulk-error-banner"
          >
            {bulk.errorBanner}
          </p>
        )}

        {!isDraftStage && (
          <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            {t('workOrders.bulk.lotBudgetHint')}
          </p>
        )}

        {bulk.cards.map((card, index) => (
          <BulkWorkOrderCard
            key={card.id}
            cardId={card.id}
            index={index}
            stage={bulk.stage}
            workOrder={card.workOrder}
            defaults={card.defaults}
            canRemove={bulk.cards.length > 1}
            onRemove={() => bulk.removeCard(card.id)}
            greenBeans={reference.greenBeans}
            machineOptions={machineOptions}
            roasterOptions={roasterOptions}
            machines={reference.machines}
            onCreateMachine={(text) => {
              setMachineDraftName(text);
              setIsMachineModalOpen(true);
            }}
            onCreateRoaster={(text) => {
              setRoasterDraftName(text);
              setIsRoasterModalOpen(true);
            }}
            canCreateMachine={canCreateMachine}
            canCreateRoaster={canCreateRoaster}
            isAtMachineTypeLimit={isAtMachineTypeLimit}
            snapshots={bulk.snapshots}
            onSnapshotChange={bulk.onSnapshotChange}
            registerHandle={bulk.registerHandle}
            unit={unit}
            setUnit={setUnit}
          />
        ))}

        {isDraftStage && (
          <div>
            <button
              type="button"
              onClick={bulk.addCard}
              disabled={atMax || readOnly}
              className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-test-id="wo-bulk-add-schedule"
            >
              <Plus className="h-4 w-4" />
              {t('workOrders.bulk.addSchedule')}
            </button>
            {atMax && (
              <p className="mt-2 text-xs text-gray-500">
                {t('workOrders.bulk.maxReached', { max: MAX_BULK_CARDS })}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('auth.common.cancel')}
          </button>
          {isDraftStage ? (
            <button
              type="button"
              onClick={bulk.createAllDrafts}
              disabled={bulk.isSubmitting || readOnly}
              className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              data-test-id="wo-bulk-save-draft-button"
            >
              {bulk.isSubmitting
                ? t('workOrders.bulk.savingDrafts')
                : t('workOrders.actions.saveAsDraft')}
            </button>
          ) : (
            <button
              type="button"
              onClick={bulk.scheduleAll}
              disabled={bulk.isSubmitting || readOnly}
              className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              data-test-id="wo-bulk-save-schedule-button"
            >
              {bulk.isSubmitting
                ? t('workOrders.bulk.scheduling')
                : t('workOrders.actions.saveAsScheduled')}
            </button>
          )}
        </div>
      </div>

      <BulkScheduleConfirmModal
        pending={bulk.pendingConfirm}
        onClose={() => bulk.setPendingConfirm(null)}
        onConfirm={bulk.confirmSchedule}
        isSubmitting={bulk.isSubmitting}
      />

      <BulkMissingFieldsModal blocked={bulk.blockedCards} onClose={() => bulk.setBlockedCards(null)} />

      <Modal
        isOpen={isMachineModalOpen}
        onClose={() => setIsMachineModalOpen(false)}
        title={t('workOrders.form.addMachineType')}
      >
        <MachineForm
          initialData={{ name: machineDraftName, isActive: true }}
          forceMode="type"
          onSubmit={handleSaveMachine}
          onCancel={() => setIsMachineModalOpen(false)}
          onTypeCreated={(item) => {
            const parentMachine = reference.machines.find((machine) => machine.id === item.machineId);
            reference.addMachine({
              id: item.id,
              name: item.name,
              isActive: item.isActive,
              isSampelRoaster: parentMachine?.isSampelRoaster ?? false,
              minimumInputKg: item.minimumInputKg,
              maximumInputKg: item.maximumInputKg,
              parentMachineId: item.machineId,
            });
            setIsMachineModalOpen(false);
            setMachineDraftName('');
            showToast(t('workOrders.form.toast.machineTypeCreated'), 'success');
          }}
        />
      </Modal>

      <Modal
        isOpen={isRoasterModalOpen}
        onClose={() => setIsRoasterModalOpen(false)}
        title={t('workOrders.form.addRoaster')}
      >
        <RoasterForm
          initialData={{ name: roasterDraftName, isActive: true }}
          onSubmit={handleSaveRoaster}
          onCancel={() => setIsRoasterModalOpen(false)}
        />
      </Modal>
    </>
  );
};
